// Фактура, която обобщава стокови разписки: банерът „Доставчикът има N стокови
// без фактура · Избери стокови", банерът с избраните („Фактурата обобщава …")
// и модалът „Кои стокови обобщава фактурата?". Стоката на стоковите НЕ се
// заприходява втори път — фактурата само ги свързва (BE:
// `stock_movements.invoice_movement_id`, НЕ `parent_movement_id`).
import { useEffect, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import type { Delivery } from "../../../../api/inventory";
import { modalStyles } from "../../../../utils/modalStyles";
import { CheckBox } from "../../components/filters/CheckBox";
import { formatMoney } from "../../components/numbers";
import {
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
} from "../../components/list/buttonStyles";
import { DeliveryPaymentPill, DeliveryTypeBadge } from "./DeliveryBadges";
import { deliveryGrossOf } from "./deliveryMeta";
import { formatQtyInput } from "./deliveryWorksheetShared";

export const DeliveryNotesOfferBanner = ({
  count,
  onPick,
}: {
  count: number;
  onPick: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-xl border border-info/30 bg-info-tint px-4 py-[13px]">
      <span className="min-w-[220px] flex-1 text-[13.5px] leading-[1.45] text-info-deep">
        {t("inventoryPage.notesOffer", { count })}
      </span>
      <button type="button" onClick={onPick} className={`${PRIMARY_BUTTON_CLASS} !py-2`}>
        {t("inventoryPage.notesPick")}
      </button>
    </div>
  );
};

export const DeliveryNotesLinkedBanner = ({
  notes,
  onChange,
}: {
  notes: Delivery[];
  onChange?: () => void;
}) => {
  const { t } = useTranslation();
  const total = notes.reduce((sum, note) => sum + deliveryGrossOf(note), 0);
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-xl border border-info/30 bg-info-tint px-4 py-3">
      <span className="flex min-w-[220px] flex-1 flex-col gap-[3px]">
        <span className="text-[13.5px] font-semibold text-info-deep">
          {t("inventoryPage.notesLinked", { count: notes.length, total: formatMoney(total) })}
        </span>
        <span className="text-[12.5px] text-body">{t("inventoryPage.notesLinkedHint")}</span>
      </span>
      {onChange && (
        <button type="button" onClick={onChange} className={SECONDARY_BUTTON_CLASS}>
          {t("inventoryPage.notesChange")}
        </button>
      )}
    </div>
  );
};

export const DeliveryNotesPickerModal = ({
  open,
  notes,
  selectedIds,
  decimalsOf,
  onClose,
  onApply,
}: {
  open: boolean;
  notes: Delivery[];
  selectedIds: number[];
  decimalsOf: (productId: number) => number;
  onClose: () => void;
  onApply: (ids: number[]) => void;
}) => {
  const { t } = useTranslation();
  const [picked, setPicked] = useState<Set<number>>(new Set(selectedIds));
  useEffect(() => {
    if (open) setPicked(new Set(selectedIds));
    // изборът се поема при отваряне
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const toggle = (id: number) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const total = notes
    .filter((note) => picked.has(note.id))
    .reduce((sum, note) => sum + deliveryGrossOf(note), 0);
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={640}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 20px)" }}
    >
      <div
        className="flex max-h-[86vh] flex-col bg-surface"
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
            event.preventDefault();
            onApply([...picked]);
          }
        }}
      >
        <div className="flex flex-col gap-[3px] border-b border-shade-2 px-[22px] py-[18px]">
          <span className="text-[17px] font-semibold text-ink">{t("inventoryPage.notesModalTitle")}</span>
          <span className="text-[13px] text-body">{t("inventoryPage.notesModalSub")}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          {notes.map((note, index) => {
            const on = picked.has(note.id);
            return (
              <button
                key={note.id}
                type="button"
                role="checkbox"
                aria-checked={on}
                autoFocus={index === 0}
                onClick={() => toggle(note.id)}
                className={`flex w-full cursor-pointer items-start gap-3 border-none px-[22px] py-[13px] text-left text-ink outline-none focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${
                  index ? "border-t border-solid border-shade-1" : ""
                } ${on ? "bg-brand-tint" : "bg-transparent hover:bg-fill"}`}
              >
                <CheckBox checked={on} />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="flex flex-wrap items-center gap-2">
                    <DeliveryTypeBadge documentType="note" />
                    <span className="font-semibold">{note.invoiceNumber || t("inventoryPage.noDocNumber")}</span>
                    <span className="text-[12.5px] text-body">
                      {dayjs(note.date).format("DD.MM.YYYY")} · {t("inventoryPage.internalNo", { id: note.id })}
                    </span>
                    {/* платена отделно — не се плаща пак през фактурата */}
                    {note.paymentStatus === "paid" && <DeliveryPaymentPill delivery={note} />}
                  </span>
                  {note.note && (
                    <span className="truncate text-[12.5px] text-muted" title={note.note}>
                      {note.note}
                    </span>
                  )}
                  <span className="flex flex-col gap-[3px] rounded-[7px] bg-fill px-2.5 py-[7px]">
                    {note.lines.slice(0, 6).map((line) => (
                      <span key={line.id} className="flex justify-between gap-2.5 text-[12.5px]">
                        <span className="truncate">{line.productName}</span>
                        <span className="whitespace-nowrap font-sheet text-ink-2">
                          {formatQtyInput(line.quantity, decimalsOf(line.productId))} × {line.unitPrice}
                        </span>
                      </span>
                    ))}
                    {note.lines.length > 6 && (
                      <span className="text-xs text-muted">
                        {t("inventoryPage.notesMoreLines", { count: note.lines.length - 6 })}
                      </span>
                    )}
                  </span>
                </span>
                <span className="font-sheet font-semibold">{formatMoney(deliveryGrossOf(note))}</span>
              </button>
            );
          })}
        </div>
        <div className="border-t border-shade-2 px-[22px] py-3 text-[12.5px] text-body">
          {t("inventoryPage.notesModalHint")}
        </div>
        <div className="flex flex-wrap items-center gap-2 border-t border-shade-2 px-[22px] pb-[18px] pt-3.5">
          <span className="text-body">
            {t("inventoryPage.notesPicked", { count: picked.size, total: formatMoney(total) })}
          </span>
          <div className="flex-1" />
          <button type="button" onClick={onClose} className={SECONDARY_BUTTON_CLASS}>
            {t("inventoryPage.cancel")}
          </button>
          <button type="button" onClick={() => onApply([...picked])} className={PRIMARY_BUTTON_CLASS}>
            {t("inventoryPage.notesApply")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
